import { FILE_UPLOAD } from '@/utils/constants'
import { formatBytes } from '@/utils/format-bytes'

// Browsers report '' or octet-stream for types they don't know (e.g. .avif on some pickers,
// .dng on Windows)
const UNKNOWN_MIME = ['', 'application/octet-stream']

const extensionOf = name => (name?.includes('.') ? name.split('.').pop().toLowerCase() : '')

// Does an <input accept> list ("image/png, image/*, .dng") admit a file? Plain string compares on
// purpose: an `accept` entry is not a pattern (`image/svg+xml` as a RegExp never matches itself).
// `.ext` entries match the file name, whatever type the browser reports for it.
export function acceptsFile(accept, file) {
  const type = (file.type || '').toLowerCase()
  const extension = `.${extensionOf(file.name)}`
  return accept
    .toLowerCase()
    .split(',')
    .map(entry => entry.trim())
    .some(entry => {
      if (entry.startsWith('.')) return entry === extension
      return entry.endsWith('/*') ? type.startsWith(entry.slice(0, -1)) : entry === type
    })
}

// A RAW photo in Adobe's DNG format, by its type or its name
const isDng = file => FILE_UPLOAD.DNG_MIME.includes((file.type || '').toLowerCase())
  || FILE_UPLOAD.DNG_EXT.includes(extensionOf(file.name))

// A video by its type, or by its name when the browser reports no usable type (as the api decides)
export function isVideoFile(file) {
  const type = (file.type || '').toLowerCase()
  return FILE_UPLOAD.VIDEO_MIME.includes(type)
    || (UNKNOWN_MIME.includes(type) && FILE_UPLOAD.VIDEO_EXT.includes(extensionOf(file.name)))
}

export function sizeLimitFor(file) {
  if (isVideoFile(file)) return FILE_UPLOAD.VIDEO_SIZE_LIMIT
  return isDng(file) ? FILE_UPLOAD.DNG_SIZE_LIMIT : FILE_UPLOAD.SIZE_LIMIT
}

// Client-side guard before uploading: an i18n key to toast, or null when the file is an
// allow-listed image, a DNG or (with `videos`) a video within its size limit. The backend
// re-validates from the bytes — this only saves a round trip (and catches HEIC/TIFF/PDF and camera
// RAW files picked on desktop).
export function fileUploadError(file, { videos = false } = {}) {
  const invalidFormat = videos ? 'page.files.add.error.invalid_format_media' : 'page.files.add.error.invalid_format'
  if (!file) return invalidFormat
  if (videos && isVideoFile(file)) {
    return file.size > FILE_UPLOAD.VIDEO_SIZE_LIMIT ? 'page.files.add.error.video_too_large' : null
  }
  const extension = extensionOf(file.name)
  if (FILE_UPLOAD.RAW_UNSUPPORTED_EXT.includes(extension)) {
    return 'page.files.add.error.raw_unsupported'
  }
  if (!isDng(file)) {
    const extOk = FILE_UPLOAD.ACCEPT_EXT.includes(extension)
    const type = (file.type || '').toLowerCase()
    const mimeOk = FILE_UPLOAD.ACCEPT_MIME.includes(type) || (UNKNOWN_MIME.includes(type) && extOk)
    if (!extOk || !mimeOk) return invalidFormat
  }
  if (file.size > sizeLimitFor(file)) return 'page.files.add.error.too_large'
  return null
}

// The line under a file's "Download original" button: "Image (jpeg) - 2.4 MB",
// "Video (mov) - 412.3 MB - 2:31" (the length once the streaming copy knows it)
export function fileInfoLine(item, t, locale) {
  const params = { format: item.fileFormatLabel, size: formatBytes(item.fileSize, { locale }) }
  if (!item.isVideo) return t('page.files.detail.file_info', params)
  const info = t('page.files.detail.video_info', params)
  return item.durationLabel ? `${info} - ${item.durationLabel}` : info
}

// Native share: the intent carries { path, name } entries — files the share extension copied
// into the app group. Read each one through `read(path)` into a File for the upload form.
// Unsupported formats are reported and never read: a HEIC or a video would otherwise be
// pulled into memory only to be rejected. The size check runs on the real File in the form
// (the size is unknown before the read).
export async function loadSharedFiles(entries, read, onReject) {
  const files = []
  for (const entry of entries) {
    const errorKey = fileUploadError(entry)
    if (errorKey) {
      onReject(errorKey, entry)
      continue
    }
    const blob = await read(entry.path)
    files.push(new File([blob], entry.name, { type: blob.type }))
  }
  return files
}
